import {
  default as Button,
  default as CustomButton,
} from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import WorkflowStatus from "@/components/WorkflowStatus";
import { capitalizeText } from "@/lib/capitalizeText";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantSchema, TenantSchemaProperty } from "@/utils/types/schemas";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { Card } from "antd";
import { Printer, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const hasSchema = (
  json: TenantSchema["json"] | {}
): json is TenantSchemaProperty | Record<string, any> => {
  return (
    typeof json !== "string" &&
    json &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (
  property: any
): property is { "x-component": string; title: string; name: string } => {
  return (
    property &&
    typeof property === "object" &&
    "x-component" in property &&
    typeof property["x-component"] === "string" &&
    "title" in property &&
    typeof property["title"] === "string"
  );
};

const ModuleFormPage: React.FC = () => {
  const navigate = useNavigate();

  const { schemaName = "", id } = useParams<{
    schemaName: string;
    id: string;
  }>();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName),
    enabled: !!schemaName,
  });

  const { data: moduleInfo, isLoading: moduleLoading } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName, id ?? ""),
    enabled: isEditMode && !!schemaName && !!id,
  });

  const handleSubmit = async () => {
    await form.validate();
    let modifiedValues = removeMetaFields(form.values);
    setIsSubmitting(true);

    if (schemaInfo?.json && hasSchema(schemaInfo.json)) {
      const properties = schemaInfo.json.schema?.properties ?? {};
      const valueKeys = Object.keys(modifiedValues);

      Object.values(properties).forEach(property => {
        if (
          hasXComponent(property) &&
          property["x-component"] === "CustomUpload"
        ) {
          if (valueKeys.includes(property["title"] ?? "")) {
            const value = modifiedValues[property["title"]];
            if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
              modifiedValues = {
                ...modifiedValues,
                [property["title"]]: value[0].id,
              };
            }
          }
        } else if (
          hasXComponent(property) &&
          property["x-component"] === "CustomImageUpload"
        ) {
          if (valueKeys.includes(property["title"] ?? "")) {
            const value = modifiedValues[property["title"]];
            if (value && typeof value === "object" && "id" in value) {
              modifiedValues = {
                ...modifiedValues,
                [property["title"]]: value.id,
              };
            }
          }
        }
      });
    } else {
      console.log("Schema not available or invalid type");
    }

    if (isEditMode && moduleInfo) {
      const originalData = removeMetaFields(moduleInfo);
      if (!checkFormChanges(modifiedValues, originalData, notify)) {
        setIsSubmitting(false);
        return;
      }
    }

    if (isEditMode) {
      const response = await DynamicModuleService.updateItem(
        schemaName,
        id!,
        modifiedValues
      );
      if (response) {
        navigate(navigationRoutes.MODULE(schemaName ?? ""));
      }
    } else {
      const response = await DynamicModuleService.createItem(
        schemaName,
        modifiedValues
      );
      if (response) {
        navigate(navigationRoutes.MODULE(schemaName ?? ""));
      }
    }
    setIsSubmitting(false);
  };
  useEffect(() => {
    if (isEditMode && moduleInfo) {
      form.setInitialValues(removeMetaFields(moduleInfo));
    }
  }, [isEditMode, moduleInfo, form]);

  if (isEditMode && moduleLoading) {
    return <LoadingSpinner />;
  }
  if (isEditMode && !moduleInfo) {
    return <NotFound entityName="Module" />;
  }
  if (schemaLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            <div className="flex w-full items-center justify-between gap-2.5">
              <span className="text-lg font-semibold">
                {isEditMode
                  ? `Edit ${capitalizeText(schemaName)}`
                  : `Add ${capitalizeText(schemaName)}`}
              </span>

              {isEditMode && (
                <WorkflowStatus schemaName={schemaName} recordId={id ?? ""} />
              )}
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              {isEditMode && (
                <Button
                  type="primary"
                  onClick={() =>
                    navigate(navigationRoutes.PRINT(schemaName ?? "", id ?? ""))
                  }
                >
                  <Printer size={16} />
                </Button>
              )}
              <CustomButton
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
                size="middle"
                title={isEditMode ? "Save Record Changes" : "Save New Record"}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                    ? "Save Changes"
                    : "Save"}
              </CustomButton>
            </div>
          }
          onBack={() => window.history.back()}
        />
      }
    >
      <SchemaFormView form={form} schemaJson={schemaInfo?.json ?? schemaInfo} />
    </Card>
  );
};

export default ModuleFormPage;
